// What the stock sheet's tabs call things, brought together. The same item is written in several ways across the
// tabs and forms ("Cookie 2 pk", "2pk cookies", "2pk cookie"; "Power rinse", "Power Rinse"), and the sheet's own
// formulas only find an item when the text is exactly the same. Here every name gets a key, and the few that differ
// in more than spelling are tied together by hand.

export const SHOPS = ["Bogstadveien", "Grünerløkka", "Steen & Strøm", "Sandvika", "Strømmen"];

export type Category = "Boxes" | "Bags" | "Serving" | "Cleaning" | "Office" | "Drinks" | "Café & kitchen";
export const CATEGORIES: Category[] = ["Boxes", "Bags", "Serving", "Cleaning", "Office", "Drinks", "Café & kitchen"];

// Names that are the same item under another name, by key.
const ALIASES: Record<string, string> = {
  plasticcup: "brandedplasticcup",
  "7upfree": "7up",
  røycejordbæreple: "røycejordbæreplejuice",
  røycerabarbraeple: "røycerabarbraeplejuice",
  tresaigoslid: "tresamigoslid",
  cookiedoughbox: "cookiedoughcup",
  // The suppliers' yearly logs.
  "8ozcoffeecup": "8ozpapercup",
  "12ozcoffeecup": "12ozpapercup",
  "80mmlid": "smallpaperlid",
  "90mmlid": "bigpaperlid",
  treslid: "tresamigoslid",
  mleske: "mlesketilbakeform",
};

/** The key two names share when they mean the same item: lower case, letters and digits only, no plural s. */
export function nameKey(name: string): string {
  let key = name.toLowerCase().normalize("NFC").replace(/[^a-z0-9æøå]+/g, "");
  const cookie = /^cookie(\d+)pk$/.exec(key);
  if (cookie) key = `${cookie[1]}pkcookie`;
  // "Stickers" is one item and "Sticker: S" another, so only a real word loses its s.
  const lastWord = name.match(/[A-Za-zÆØÅæøå]+/g)?.at(-1) ?? "";
  if (lastWord.length > 2 && key.endsWith("s")) key = key.slice(0, -1);
  return ALIASES[key] ?? key;
}

const KINDS: [Category, RegExp][] = [
  ["Boxes", /insert|cupcake|pkcookie|kakeeske|bakeform|eske|icecreamtray/],
  ["Bags", /^(cookie|blue|pink|yellow|smalltakeaway|bigtakeaway)bag$/],
  ["Serving", /cup$|lid$|^straw$|spoon|napkin/],
  ["Cleaning", /mop|cloth|sponge|swiffer|trashbag|glove|^d2$|^d10$|^power|^jif|soap|sanitizer|paperroll/],
  ["Office", /staple|^tape$|stickynote|receipt|sticker/],
  ["Drinks", /pepsi|^solo$|7up|røyce|soda/],
];
export const categoryOf = (key: string): Category => KINDS.find(([, test]) => test.test(key))?.[0] ?? "Café & kitchen";

// "5 days", "6 weeks", "4 months" as days.
export function leadDays(text: string | undefined): number | null {
  const match = /^\s*(\d+(?:\.\d+)?)\s*(day|week|month)/i.exec(text ?? "");
  if (!match) return null;
  const size = { day: 1, week: 7, month: 30 }[match[2]!.toLowerCase() as "day" | "week" | "month"];
  return Math.round(Number(match[1]) * size);
}
